import { Delete, Edit, ExpandMore, Folder, FolderOpen, Visibility, VisibilityOff } from '@mui/icons-material';
import { Grid } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TagModel } from 'features/motive/survey.types';
import React from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { deleteSurveyTag } from '../../../API/surveys';
import { BasicConfirmDialog } from '../../ui/base-dialogs';

interface Props {
  tag: TagModel;
  index: number;
  expanded: boolean;
  tagName: string;
  tagId: string;
  selectionCount: number;
  draggingExist: boolean;
  token: string;
  children?: React.ReactNode;

  handleChange(event: React.ChangeEvent<any>, isExpanded: boolean): void;

  deleteTag(tagId: string): void;

  handleEditTagClick(tagId: string): (event: React.SyntheticEvent) => void;

  handleToggleVisibility(tagId: string, visibility?: boolean): () => void;

  handleOpenConfirmDeleteSurveyDialog(currentTag: TagModel): () => void;
}

const SurveyItem: React.FC<Props> = ({
  draggingExist,
  handleChange,
  tagName,
  tagId,
  expanded,
  children,
  selectionCount,
  tag,
  deleteTag,
  handleEditTagClick,
  handleOpenConfirmDeleteSurveyDialog,
  handleToggleVisibility,
}) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const [tagDeleteOpen, setTagDeleteOpen] = React.useState<boolean>(false);
  const [dialogLoading, setDialogLoading] = React.useState<boolean>(false);

  const handleTagDelete = async () => {
    if (tag.surveys.length > 0) {
      setTagDeleteOpen(true);
    } else {
      const res = await deleteSurveyTag(tag.tagId);
      if (res) {
        // If deletion happened successfully, delete the tag from the view
        deleteTag(tagId);
      }
    }
  };

  const handleTagDeleteConfirm = async () => {
    setDialogLoading(true);
    const res = await deleteSurveyTag(tag.tagId);
    if (res) {
      setTagDeleteOpen(false);
      deleteTag(tagId);
    }
    setDialogLoading(false);
  };

  const handleTagDeleteClose = () => {
    setTagDeleteOpen(false);
  };

  return (
    <Grid container={true}>
      <Droppable droppableId={tagId} key={'droppable' + tagId}>
        {(analysisProvided, analysisSnapshot) => (
          <Accordion
            {...analysisProvided.droppableProps}
            ref={analysisProvided.innerRef}
            expanded={expanded || analysisSnapshot.isDraggingOver}
            onChange={handleChange}
            className={cx(classes.panel, {
              [classes.draggingOver]: analysisSnapshot.isDraggingOver,
            })}
            elevation={0}
          >
            {selectionCount === 0 || analysisSnapshot.isDraggingOver || draggingExist ? (
              <AccordionSummary className={classes.summary} expandIcon={<ExpandMore />}>
                <div className={cx(classes.root, classes.heading)}>
                  {expanded ? (
                    <FolderOpen className={classes.folderIcon} color="primary" />
                  ) : (
                    <Folder className={classes.folderIcon} color="primary" />
                  )}
                  <Typography noWrap={true}>{tagName}</Typography>
                </div>
                {tag.isOwn && (
                  <div className={classes.tagActions}>
                    {tag.visible ? (
                      <Tooltip title={t('makeInvisible')!}>
                        <IconButton onClick={handleToggleVisibility(tag.tagId, !tag.visible)} size="large">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('makeVisible')!}>
                        <IconButton onClick={handleToggleVisibility(tag.tagId, !tag.visible)} size="large">
                          <VisibilityOff />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('edit')!}>
                      <IconButton onClick={handleEditTagClick(tag.tagId)} size="large">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('delete')!}>
                      <IconButton color="secondary" onClick={handleTagDelete} size="large">
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </AccordionSummary>
            ) : (
              <AccordionSummary className={classes.selectionSummary}>
                <Typography className={classes.heading} variant="body1">
                  {t('analysisSelected', { count: selectionCount })}
                </Typography>
                <Tooltip title={t('delete')!}>
                  <IconButton aria-label="Delete" onClick={handleOpenConfirmDeleteSurveyDialog(tag)} size="large">
                    <Delete />
                  </IconButton>
                </Tooltip>
              </AccordionSummary>
            )}
            <AccordionDetails className={classes.content}>{children}</AccordionDetails>
            {analysisProvided.placeholder}
          </Accordion>
        )}
      </Droppable>
      <BasicConfirmDialog
        open={tagDeleteOpen}
        title={t('surveyDelete')}
        confirmButton={t('delete')}
        cancelButton={t('cancel')}
        handleClose={handleTagDeleteClose}
        handleConfirm={handleTagDeleteConfirm}
        loading={dialogLoading}
      >
        <Typography variant="body1">{t('surveyDeleteWarning', { surveyName: tag.tagName, count: tag.surveys.length })}</Typography>
      </BasicConfirmDialog>
    </Grid>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    margin: 1,
  },
  panel: {
    minHeight: 50,
    flexGrow: 1,
    border: `1px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(0.5),
  },
  draggingOver: {
    backgroundColor: theme.palette.grey[700],
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.sharp,
    }),
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  heading: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(15),
    width: '40%',
    flexShrink: 0,
  },
  dragHandler: {
    paddingTop: '6.1px', // Bug in the dnd-styles-applied
    marginLeft: theme.spacing(1),
  },
  tagActions: {
    display: 'flex',
    alignItems: 'center',
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
  },
  moreInfoBorder: {
    border: '5px',
    borderColor: theme.palette.primary.dark,
  },
  folderIcon: {
    marginRight: theme.spacing(1),
  },
  awLink: {
    textDecoration: 'none',
    display: 'none',
    width: '150px',
  },
  summary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: 0,
    },
  },
  selectionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: 0,
      backgroundColor: theme.palette.secondary.light,
      borderRadius: 5,
    },
  },
}));

export default React.memo(SurveyItem);
